import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, Save, Percent, Loader2, Search } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';
import CategorySelector from '../components/CategorySelector';
import UniversalList from '../components/UniversalList';

const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) { localStorage.removeItem('token'); window.location.href = '/login'; throw new Error('Session expired'); }
  return res.json();
};

const PAGE_SIZE_DEFAULT = 20;

const ProfitRatesPage = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission('settings', 'edit');

  const [defaultRate, setDefaultRate] = useState('0.0000');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaultSaving, setDefaultSaving] = useState(false);
  const [newCategoryId, setNewCategoryId] = useState(null);
  const [newRate, setNewRate] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/settings/profit-rates');
      if (res.status === 'success') {
        setDefaultRate(res.data.default_rate || '0.0000');
        setItems(res.data.items || []);
      }
    } catch (e) {
      notify(t('common.error') || 'Failed to load profit rates', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Reset page on search
  useEffect(() => { setPage(1); }, [searchTerm]);

  // Client-side filter
  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    const q = searchTerm.toLowerCase();
    return items.filter(i => (i.category_name || '').toLowerCase().includes(q));
  }, [items, searchTerm]);

  // Client-side paginate
  const totalItems = filteredItems.length;
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  const handleSaveDefault = async () => {
    if (!canEdit) return;
    setDefaultSaving(true);
    try {
      const res = await apiFetch('/api/settings/profit-rates/save-default', {
        method: 'POST',
        body: JSON.stringify({ rate: parseFloat(defaultRate) || 0 }),
      });
      if (res.status === 'success') {
        notify(t('common.saved') || 'Saved', 'success');
      } else {
        notify(res.message || 'Error', 'error');
      }
    } catch (e) {
      notify('Error saving default rate', 'error');
    } finally {
      setDefaultSaving(false);
    }
  };

  const handleSaveItem = async (item) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await apiFetch('/api/settings/profit-rates/save', {
        method: 'POST',
        body: JSON.stringify({
          category_id: item.category_id,
          profit_rate: parseFloat(item.profit_rate) || 0,
        }),
      });
      if (res.status === 'success') {
        notify(t('common.saved') || 'Saved', 'success');
        await loadData();
      } else {
        notify(res.message || 'Error', 'error');
      }
    } catch (e) {
      notify('Error saving profit rate', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await apiFetch('/api/settings/profit-rates/delete', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      if (res.status === 'success') {
        notify(t('common.deleted') || 'Deleted', 'success');
        setItems(prev => prev.filter(i => i.id !== id));
      } else {
        notify(res.message || 'Error', 'error');
      }
    } catch (e) {
      notify('Error deleting profit rate', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNew = async () => {
    if (!newCategoryId || !newRate) {
      notify(t('common.required') || 'Please fill in all fields', 'error');
      return;
    }
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await apiFetch('/api/settings/profit-rates/save', {
        method: 'POST',
        body: JSON.stringify({
          category_id: newCategoryId,
          profit_rate: parseFloat(newRate) || 0,
        }),
      });
      if (res.status === 'success') {
        notify(t('common.saved') || 'Saved', 'success');
        setNewCategoryId(null);
        setNewRate('');
        setAddingNew(false);
        await loadData();
      } else {
        notify(res.message || 'Error', 'error');
      }
    } catch (e) {
      notify('Error saving profit rate', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleItemRateChange = (id, newRate) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, profit_rate: newRate } : i));
  };

  const columns = [
    {
      key: 'id',
      label: '#',
      className: 'w-12 text-slate-500',
    },
    {
      key: 'category_name',
      label: t('category') || 'Category',
      className: 'text-slate-700 dark:text-slate-300',
    },
    {
      key: 'profit_rate',
      label: t('settings.profit_rate_percent') || 'Rate (%)',
      render: (val, row) => {
        if (!canEdit) return <span className="text-slate-700 dark:text-slate-300">{val}%</span>;
        return (
          <input type="number" step="0.0001" min="0" max="100"
            value={val}
            onChange={e => handleItemRateChange(row.id, e.target.value)}
            className="w-28 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-sm focus:ring-2 focus:ring-blue-500"
          />
        );
      },
    },
    {
      key: 'updated_at',
      label: t('common.updated_at') || 'Updated',
      className: 'text-slate-500',
      render: (val) => val?.substring(0, 10) || '-',
    },
    ...(canEdit ? [{
      key: '_actions',
      label: t('common.actions') || 'Actions',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => handleSaveItem(row)} disabled={saving}
            className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            title={t('common.save') || 'Save'}>
            <Save size={15} />
          </button>
          <button onClick={() => handleDeleteItem(row.id)} disabled={saving}
            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title={t('common.delete') || 'Delete'}>
            <Trash2 size={15} />
          </button>
        </div>
      ),
    }] : []),
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Title */}
      <div>
        <h1 className="text-2xl font-bold">
          {t('settings.profit_rates') || 'Profit Management'}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          {t('settings.profit_rates_desc') || 'Set default profit rate and category-specific profit rates'}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-slate-400" size={24} />
        </div>
      ) : (
        <>
          {/* Default Rate */}
          <div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">
              {t('settings.default_profit_rate') || 'Default Profit Rate'}
            </h2>
            <div className="flex items-end gap-3 max-w-md">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                  {t('settings.profit_rate_percent') || 'Rate (%)'}
                </label>
                <div className="relative">
                  <input type="number" step="0.0001" min="0" max="100"
                    value={defaultRate}
                    onChange={e => setDefaultRate(e.target.value)}
                    disabled={!canEdit}
                    className="w-full px-3 py-2 pr-8 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 disabled:opacity-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <Percent size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
              {canEdit && (
                <button onClick={handleSaveDefault} disabled={defaultSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                  {defaultSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {t('common.save') || 'Save'}
                </button>
              )}
            </div>
          </div>

          {/* Category Rates */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                {t('settings.category_profit_rates') || 'Category Profit Rates'}
              </h2>
              {canEdit && !addingNew && (
                <button onClick={() => setAddingNew(true)}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 text-sm">
                  <Plus size={16} />
                  {t('common.add') || 'Add'}
                </button>
              )}
            </div>

            {/* Add new form */}
            {addingNew && (
              <div className="mb-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                      {t('category') || 'Category'}
                    </label>
                    <CategorySelector value={newCategoryId} onChange={setNewCategoryId} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                      {t('settings.profit_rate_percent') || 'Rate (%)'}
                    </label>
                    <input type="number" step="0.0001" min="0" max="100"
                      value={newRate}
                      onChange={e => setNewRate(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <button onClick={handleAddNew} disabled={saving}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5">
                      {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                      {t('common.save') || 'Save'}
                    </button>
                    <button onClick={() => { setAddingNew(false); setNewCategoryId(null); setNewRate(''); }}
                      className="px-3 py-2 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
                      {t('common.cancel') || 'Cancel'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="relative w-full md:max-w-sm mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={18} />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={t('common.search') + '...'}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white text-sm"
              />
            </div>
            <UniversalList
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              columns={columns}
              data={pageData}
              loading={false}
              emptyMessage={t('common.no_data') || 'No data'}
              rowKey="id"
              page={page}
              totalItems={totalItems}
              pageSize={pageSize}
              setPage={setPage}
              setPageSize={setPageSize}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ProfitRatesPage;
